class LomaraMenu extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-menu-toggle]');
    this.panel = this.querySelector('[data-menu-panel]');
    if (!this.button || !this.panel) return;
    this.button.addEventListener('click', () => this.toggle());
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });
  }

  toggle() {
    const open = this.button.getAttribute('aria-expanded') === 'true';
    this.button.setAttribute('aria-expanded', String(!open));
    this.panel.hidden = open;
    document.body.classList.toggle('menu-open', !open);
  }

  close() {
    this.button.setAttribute('aria-expanded', 'false');
    this.panel.hidden = true;
    document.body.classList.remove('menu-open');
    this.button.focus();
  }
}

if (!customElements.get('lomara-menu')) customElements.define('lomara-menu', LomaraMenu);

class LomaraPairingCarousel extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-carousel-viewport]');
    this.cards = [...this.querySelectorAll('[data-carousel-card]')];
    this.dots = this.querySelector('[data-carousel-dots]');
    if (!this.viewport || !this.cards.length || !this.dots) return;

    this.handleScroll = () => {
      window.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = window.requestAnimationFrame(() => this.updateActiveDot());
    };
    this.handleResize = () => this.renderDots();

    this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize);
    this.renderDots();
  }

  disconnectedCallback() {
    this.viewport?.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    window.cancelAnimationFrame(this.scrollFrame);
  }

  getItemsPerView() {
    const card = this.cards[0];
    if (!card) return 1;
    const gap = parseFloat(window.getComputedStyle(this.querySelector('[data-carousel-track]')).columnGap) || 0;
    return Math.max(1, Math.round(this.viewport.clientWidth / (card.getBoundingClientRect().width + gap)));
  }

  getTargets() {
    const itemsPerView = this.getItemsPerView();
    const pageCount = Math.ceil(this.cards.length / itemsPerView);
    const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
    return Array.from({ length: pageCount }, (_, page) => {
      const cardIndex = Math.min(page * itemsPerView, this.cards.length - 1);
      return {
        cardIndex,
        left: Math.min(this.cards[cardIndex].offsetLeft, maxScroll)
      };
    });
  }

  renderDots() {
    this.targets = this.getTargets();
    this.dots.replaceChildren();

    this.targets.forEach((target, index) => {
      const firstLabel = this.cards[target.cardIndex].dataset.carouselLabel;
      const lastIndex = Math.min(target.cardIndex + this.getItemsPerView() - 1, this.cards.length - 1);
      const lastLabel = this.cards[lastIndex].dataset.carouselLabel;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'pairing-carousel-dot';
      button.setAttribute('aria-label', firstLabel === lastLabel ? `Show ${firstLabel}` : `Show ${firstLabel} and ${lastLabel}`);
      button.addEventListener('click', () => {
        this.viewport.scrollTo({ left: target.left, behavior: 'smooth' });
        this.setActiveDot(index);
      });
      this.dots.append(button);
    });

    this.updateActiveDot();
  }

  updateActiveDot() {
    if (!this.targets?.length) return;
    const activeIndex = this.targets.reduce((closest, target, index) => {
      return Math.abs(this.viewport.scrollLeft - target.left) < Math.abs(this.viewport.scrollLeft - this.targets[closest].left) ? index : closest;
    }, 0);
    this.setActiveDot(activeIndex);
  }

  setActiveDot(activeIndex) {
    [...this.dots.children].forEach((dot, index) => {
      const active = index === activeIndex;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }
}

if (!customElements.get('lomara-pairing-carousel')) customElements.define('lomara-pairing-carousel', LomaraPairingCarousel);

class LomaraProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.url || this.dataset.loaded === 'true') return;
    this.abortController = new AbortController();
    this.load = this.load.bind(this);

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        this.observer.disconnect();
        this.load();
      }, { rootMargin: '0px 0px 400px 0px' });
      this.observer.observe(this);
    } else {
      this.load();
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.abortController?.abort();
  }

  async load() {
    this.dataset.loaded = 'true';

    try {
      const response = await fetch(this.dataset.url, { signal: this.abortController.signal });
      if (!response.ok) throw new Error(String(response.status));
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const recommendations = html.querySelector('lomara-product-recommendations');

      if (recommendations?.innerHTML.trim()) {
        this.innerHTML = recommendations.innerHTML;
      }
    } catch (error) {
      if (error.name !== 'AbortError') this.dataset.loadError = 'true';
    } finally {
      this.setAttribute('aria-busy', 'false');
    }
  }
}

if (!customElements.get('lomara-product-recommendations')) customElements.define('lomara-product-recommendations', LomaraProductRecommendations);

class LomaraProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form[action*="/cart/add"]');
    this.optionInputs = [...this.querySelectorAll('[data-product-option]')];
    this.variantInput = this.querySelector('[data-variant-id]');
    this.submitButton = this.querySelector('[name="add"]');
    this.submitLabel = this.querySelector('[data-product-submit-label]');
    this.price = this.querySelector('[data-product-price]');
    this.comparePrice = this.querySelector('[data-product-compare]');
    this.status = this.querySelector('[data-product-status]');

    const variantData = this.querySelector('[data-product-variants]');
    if (!this.form || !variantData || !this.variantInput || !this.submitButton) return;

    try {
      this.variants = JSON.parse(variantData.textContent);
    } catch (error) {
      this.variants = [];
      return;
    }

    this.optionInputs.forEach((input) => {
      input.addEventListener('change', () => this.updateVariant());
    });
  }

  updateVariant() {
    const selectedOptions = this.optionInputs.map((input) => input.value);
    const variant = this.variants.find((candidate) => {
      return candidate.options.every((option, index) => option === selectedOptions[index]);
    });

    if (!variant) {
      this.variantInput.value = '';
      this.submitButton.disabled = true;
      this.submitLabel.textContent = this.submitButton.dataset.unavailableText;
      this.status.textContent = this.submitButton.dataset.unavailableText;
      return;
    }

    this.variantInput.value = variant.id;
    this.submitButton.disabled = !variant.available;
    this.submitLabel.textContent = variant.available
      ? this.submitButton.dataset.addText
      : this.submitButton.dataset.soldOutText;
    this.status.textContent = variant.available ? '' : this.submitButton.dataset.soldOutText;
    this.updatePrice(variant);

    const url = new URL(this.dataset.productUrl, window.location.origin);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
  }

  updatePrice(variant) {
    if (this.price) this.price.textContent = this.formatMoney(variant.price);
    if (!this.comparePrice) return;

    const hasComparePrice = variant.compare_at_price && variant.compare_at_price > variant.price;
    this.comparePrice.hidden = !hasComparePrice;
    this.comparePrice.textContent = hasComparePrice ? this.formatMoney(variant.compare_at_price) : '';
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: this.dataset.currency
      }).format(cents / 100);
    } catch (error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }
}

if (!customElements.get('lomara-product-form')) customElements.define('lomara-product-form', LomaraProductForm);

class LomaraQuickAdd extends HTMLElement {
  connectedCallback() {
    this.openButton = this.querySelector('[data-quick-add-open]');
    this.dialog = this.querySelector('[data-quick-add-dialog]');
    this.closeButton = this.querySelector('[data-quick-add-close]');
    this.form = this.querySelector('form[action*="/cart/add"]');
    this.optionInputs = [...this.querySelectorAll('[data-quick-add-option]')];
    this.variantInput = this.querySelector('[data-quick-add-variant-id]');
    this.submitButton = this.querySelector('[data-quick-add-submit]');
    this.submitLabel = this.querySelector('[data-quick-add-label]');
    this.status = this.querySelector('[data-quick-add-status]');
    this.mainImage = this.querySelector('[data-quick-add-image]');
    this.thumbnails = [...this.querySelectorAll('[data-quick-add-thumbnail]')];

    const variantData = this.querySelector('[data-quick-add-variants]');
    if (!this.openButton || !this.dialog || !this.closeButton || !this.form || !this.variantInput || !this.submitButton || !this.submitLabel || !this.status || !variantData) return;

    try {
      this.variants = JSON.parse(variantData.textContent);
    } catch (error) {
      this.variants = [];
    }

    this.handleSubmit = (event) => this.submit(event);
    this.handleOpen = () => this.dialog.showModal();
    this.handleCloseClick = () => this.dialog.close();
    this.handleDialogClick = (event) => {
      if (event.target === this.dialog) this.dialog.close();
    };
    this.handleDialogClose = () => {
      this.status.textContent = '';
      this.updateVariant();
      this.openButton.focus();
    };
    this.handleOptionChange = () => this.updateVariant();
    this.handleThumbnailClick = (event) => this.selectImage(event.currentTarget);

    this.form.addEventListener('submit', this.handleSubmit);
    this.openButton.addEventListener('click', this.handleOpen);
    this.closeButton.addEventListener('click', this.handleCloseClick);
    this.dialog.addEventListener('click', this.handleDialogClick);
    this.dialog.addEventListener('close', this.handleDialogClose);
    this.optionInputs.forEach((input) => input.addEventListener('change', this.handleOptionChange));
    this.thumbnails.forEach((thumbnail) => thumbnail.addEventListener('click', this.handleThumbnailClick));
    this.updateVariant();
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.openButton?.removeEventListener('click', this.handleOpen);
    this.closeButton?.removeEventListener('click', this.handleCloseClick);
    this.dialog?.removeEventListener('click', this.handleDialogClick);
    this.dialog?.removeEventListener('close', this.handleDialogClose);
    this.optionInputs?.forEach((input) => input.removeEventListener('change', this.handleOptionChange));
    this.thumbnails?.forEach((thumbnail) => thumbnail.removeEventListener('click', this.handleThumbnailClick));
  }

  updateVariant() {
    const selectedOptions = this.optionInputs.map((input) => input.value);
    const variant = this.optionInputs.length
      ? this.variants.find((candidate) => candidate.options.every((option, index) => option === selectedOptions[index]))
      : this.variants[0];

    this.currentVariant = variant;
    this.status.textContent = '';

    if (!variant) {
      this.variantInput.value = '';
      this.submitButton.disabled = true;
      this.submitLabel.textContent = this.submitButton.dataset.unavailableText;
      return;
    }

    this.variantInput.value = variant.id;
    this.submitButton.disabled = !variant.available;
    this.submitLabel.textContent = variant.available
      ? this.submitButton.dataset.addText
      : this.submitButton.dataset.soldOutText;
  }

  selectImage(thumbnail) {
    if (!this.mainImage || !thumbnail.dataset.imageSrc) return;
    this.mainImage.src = thumbnail.dataset.imageSrc;
    this.mainImage.srcset = thumbnail.dataset.imageSrcset || '';
    this.mainImage.alt = thumbnail.dataset.imageAlt || '';
    if (thumbnail.dataset.imageWidth) this.mainImage.width = Number(thumbnail.dataset.imageWidth);
    if (thumbnail.dataset.imageHeight) this.mainImage.height = Number(thumbnail.dataset.imageHeight);
    this.thumbnails.forEach((item) => {
      const active = item === thumbnail;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  async submit(event) {
    event.preventDefault();
    this.submitButton.disabled = true;
    this.submitLabel.textContent = this.dataset.addingText;
    this.status.textContent = '';

    try {
      const response = await fetch(`${window.Lomara.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(this.form)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || this.dataset.errorText);

      const cartResponse = await fetch(`${window.Lomara.routes.cart}.js`, {
        headers: { Accept: 'application/json' }
      });
      const cart = await cartResponse.json();
      document.querySelectorAll('[data-cart-count]').forEach((count) => {
        count.textContent = cart.item_count;
      });
      this.submitLabel.textContent = this.dataset.addedText;
      this.status.textContent = this.dataset.addedText;
    } catch (error) {
      this.submitLabel.textContent = this.dataset.errorText;
      this.status.textContent = error.message || this.dataset.errorText;
    } finally {
      this.submitButton.disabled = !this.currentVariant?.available;
    }
  }
}

if (!customElements.get('lomara-quick-add')) customElements.define('lomara-quick-add', LomaraQuickAdd);

class LomaraImagePreview extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-image-preview-dialog]');
    this.image = this.querySelector('[data-image-preview-image]');
    this.titleElement = this.querySelector('[data-image-preview-title]');
    this.closeButton = this.querySelector('[data-image-preview-close]');
    if (!this.dialog || !this.image || !this.titleElement || !this.closeButton) return;

    this.handleTriggerClick = (event) => {
      const trigger = event.target.closest('[data-image-preview-trigger]');
      if (!trigger || !this.contains(trigger) || event.defaultPrevented || event.button !== 0) return;
      event.preventDefault();
      this.open(trigger);
    };
    this.handleBackdropClick = (event) => {
      if (event.target === this.dialog) this.dialog.close();
    };
    this.handleClose = () => {
      this.image.removeAttribute('src');
      this.activeTrigger?.focus();
      this.activeTrigger = null;
    };
    this.handleCloseClick = () => this.dialog.close();

    this.addEventListener('click', this.handleTriggerClick);
    this.closeButton.addEventListener('click', this.handleCloseClick);
    this.dialog.addEventListener('click', this.handleBackdropClick);
    this.dialog.addEventListener('close', this.handleClose);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleTriggerClick);
    this.closeButton?.removeEventListener('click', this.handleCloseClick);
    this.dialog?.removeEventListener('click', this.handleBackdropClick);
    this.dialog?.removeEventListener('close', this.handleClose);
  }

  open(trigger) {
    this.activeTrigger = trigger;
    this.image.src = trigger.dataset.previewSrc;
    this.image.alt = trigger.dataset.previewAlt || '';
    this.titleElement.textContent = trigger.dataset.previewTitle || '';
    this.dialog.showModal();
  }
}

if (!customElements.get('lomara-image-preview')) customElements.define('lomara-image-preview', LomaraImagePreview);

class LomaraProductShare extends HTMLElement {
  connectedCallback() {
    this.nativeButton = this.querySelector('[data-native-share]');
    this.copyButton = this.querySelector('[data-copy-share]');
    this.status = this.querySelector('[data-share-status]');
    this.shareData = {
      title: this.dataset.shareTitle,
      text: this.dataset.shareText,
      url: this.dataset.shareUrl
    };

    this.handleNativeShare = () => this.share();
    this.handleCopy = () => this.copy();

    if (this.nativeButton && navigator.share) {
      this.nativeButton.hidden = false;
      this.nativeButton.addEventListener('click', this.handleNativeShare);
    }
    this.copyButton?.addEventListener('click', this.handleCopy);
  }

  disconnectedCallback() {
    this.nativeButton?.removeEventListener('click', this.handleNativeShare);
    this.copyButton?.removeEventListener('click', this.handleCopy);
    window.clearTimeout(this.statusTimer);
  }

  async share() {
    try {
      await navigator.share(this.shareData);
      this.showStatus(this.dataset.shareSuccess);
    } catch (error) {
      if (error.name !== 'AbortError') this.showStatus(this.dataset.shareError);
    }
  }

  async copy() {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(this.dataset.shareUrl);
      } else {
        const input = document.createElement('textarea');
        input.value = this.dataset.shareUrl;
        input.setAttribute('readonly', '');
        input.style.position = 'fixed';
        input.style.opacity = '0';
        document.body.append(input);
        input.select();
        document.execCommand('copy');
        input.remove();
      }
      this.showStatus(this.dataset.copySuccess);
    } catch (error) {
      this.showStatus(this.dataset.shareError);
    }
  }

  showStatus(message) {
    if (!this.status) return;
    this.status.textContent = message;
    window.clearTimeout(this.statusTimer);
    this.statusTimer = window.setTimeout(() => {
      this.status.textContent = '';
    }, 3000);
  }
}

if (!customElements.get('lomara-product-share')) customElements.define('lomara-product-share', LomaraProductShare);

document.addEventListener('shopify:section:load', () => {
  document.documentElement.classList.remove('no-js');
});
